/*
 * Copyright 2026 Sony Corporation
 */
import { liveSteps, type ChatEvent, type LogEntry, type Roadmap, type Run } from '@ips-jit/schema';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { useDeviceLog } from '../hooks/useDeviceLog';
import { rememberDevice } from '../services/device-choice';

import { DesignOptions, designChoiceOf, type DesignChoiceState } from './DesignOptions';
import { SendIcon } from './icons';
import { Markdown } from './Markdown';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const ACTIVE_REFRESH_MS = 2_000;
const IDLE_REFRESH_MS = 10_000;

/** A run is working while it plans or builds; otherwise it is finished or waiting for the user. */
export const isWorking = (run: Run | null): boolean => run?.status === 'planning' || run?.status === 'implementing';

/** Whether a message can be sent now: not while a roadmap is planned, but while one runs (it revises its steps). */
export const canWrite = (run: Run | null, roadmap: Roadmap | null): boolean =>
  run?.status !== 'planning' &&
  (run?.status !== 'implementing' || (roadmap !== null && ['running', 'paused'].includes(roadmap.status)));

/** What the assistant is doing, in a line. */
export const workingText = (run: Run, roadmap: Roadmap | null): string => {
  if (run.status === 'planning') {
    return 'Reading the code and planning the roadmap...';
  }
  const steps = roadmap ? liveSteps(roadmap) : [];
  const n = steps.findIndex((s) => s.step_id === run.step_id) + 1;
  return n > 0
    ? `Building step ${n} of ${steps.length}: ${steps[n - 1].title}. You can write while it builds.`
    : 'Building the app...';
};

/** Requests shown in an empty chat so a new project has somewhere to start. */
export const SAMPLE_PROMPTS = [
  'When I press Capture, take a picture with every camera, read the QR code in each picture and show its URL next to the thumbnail.',
  'Every 10 minutes, capture from all cameras, count the people in each picture and show the counts per camera as a chart.',
] as const;

const clock = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const Bubble = ({ who, at, children }: { who: 'You' | 'AI'; at: string; children: React.ReactNode }) => (
  <Stack spacing={0.25} sx={{ alignSelf: who === 'You' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, textAlign: who === 'You' ? 'right' : 'left' }}>
      {who} {clock(at)}
    </Typography>
    {children}
  </Stack>
);

const Event = ({ event }: { event: ChatEvent }) => {
  switch (event.type) {
    case 'user':
      return (
        <Bubble who="You" at={event.created_at}>
          <Paper sx={{ p: 1.5, bgcolor: 'primary.dark' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {event.text}
            </Typography>
          </Paper>
        </Bubble>
      );
    case 'plan':
      return (
        <Bubble who="AI" at={event.created_at}>
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.main' }}>
            <Typography variant="overline" color="primary.light">
              Roadmap
            </Typography>
            <Markdown>{event.text}</Markdown>
          </Paper>
        </Bubble>
      );
    case 'assistant':
      return (
        <Bubble who="AI" at={event.created_at}>
          <Paper sx={{ p: 1.5 }}>
            <Markdown>{event.text}</Markdown>
            {event.version && (
              <Chip size="small" color="success" variant="outlined" label={`Version ${event.version}`} sx={{ mt: 1 }} />
            )}
          </Paper>
        </Bubble>
      );
    case 'error':
      return <Alert severity="error">{event.text}</Alert>;
    case 'status': {
      const [label, ...rest] = event.text.split(/:\s(.*)/s);
      return (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', textAlign: 'center' }}>
          {rest.length > 0 && (
            <Box component="span" sx={{ color: 'info.light', fontWeight: 700 }}>
              {label}:{' '}
            </Box>
          )}
          {rest.length > 0 ? rest[0] : event.text}
        </Typography>
      );
    }
    default:
      return (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
          {event.text}
        </Typography>
      );
  }
};

const logColor = (entry: LogEntry) =>
  entry.level === 'error' ? 'error' : entry.level === 'warn' ? 'warning' : 'default';

/** One line the app on the device reported, shown where it happened in the conversation. */
const LogLine = ({ entry }: { entry: LogEntry }) => (
  <Paper variant="outlined" sx={{ p: 1, alignSelf: 'stretch' }}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        Device log {new Date(entry.ts).toLocaleTimeString()} v{entry.version}
      </Typography>
      <Chip size="small" label={entry.kind} color={logColor(entry)} />
      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {entry.message}
      </Typography>
    </Stack>
  </Paper>
);

type Item =
  { kind: 'event'; event: ChatEvent } | { kind: 'steps'; events: ChatEvent[] } | { kind: 'log'; entry: LogEntry };

/**
 * One timeline of the conversation and the device log, each in its own order. Consecutive steps of the assistant
 * become one item, so the result is not buried under them.
 */
const timeline = (events: ChatEvent[], entries: LogEntry[]): Item[] => {
  const items: Item[] = [];
  let i = 0;
  let j = 0;
  while (i < events.length || j < entries.length) {
    if (i < events.length && (j >= entries.length || Date.parse(events[i].created_at) <= Date.parse(entries[j].ts))) {
      const event = events[i++];
      const last = items.at(-1);
      if (event.type !== 'step') {
        items.push({ kind: 'event', event });
      } else if (last?.kind === 'steps') {
        last.events.push(event);
      } else {
        items.push({ kind: 'steps', events: [event] });
      }
    } else {
      items.push({ kind: 'log', entry: entries[j++] });
    }
  }
  return items;
};

/** The steps of one stretch of work: collapsed, showing the latest, open on request. */
const Steps = ({ events }: { events: ChatEvent[] }) => (
  <Accordion disableGutters variant="outlined" sx={{ alignSelf: 'stretch' }}>
    <AccordionSummary>
      <Typography variant="caption" color="text.secondary" noWrap>
        {events.length === 1 ? '1 step' : `${events.length} steps`}: {events[events.length - 1].text}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={0.5}>
        {events.map((event) => (
          <Typography key={event.seq} variant="body2" color="text.secondary">
            {event.text}
          </Typography>
        ))}
      </Stack>
    </AccordionDetails>
  </Accordion>
);

export interface ProjectChatProps {
  projectId: string;
  /** The device the app is built for; empty when there is none. */
  deviceId: string;
  /** Whether the device log in the conversation holds only errors. */
  errorsOnly: boolean;
  /** Told what the latest request is doing and the roadmap, for the roadmap panel. */
  onProgress?: (progress: { run: Run | null; roadmap: Roadmap | null; step?: string }) => void;
  /** Bumped by the page when it changed the roadmap itself, so the chat reads it again at once. */
  refreshKey?: number;
}

/**
 * The conversation with the assistant and the device log in one place: describe the app, read its plan, approve it,
 * watch it build, and see what the app reports. The assistant works in the cloud for minutes, so the page asks for
 * what is new every couple of seconds while it does.
 */
export const ProjectChat = ({ projectId, deviceId, errorsOnly, onProgress, refreshKey }: ProjectChatProps) => {
  const { api } = useAuth();
  const [events, setEvents] = useState<ChatEvent[]>([]);
  const [run, setRun] = useState<Run | null>(null);
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const lastSeq = useRef(0);
  const polling = useRef(false);
  const end = useRef<HTMLDivElement>(null);
  const log = useDeviceLog(projectId, errorsOnly);

  const refresh = useCallback(async () => {
    // Slow answers must not pile up: one request at a time keeps the events in order.
    if (polling.current) {
      return;
    }
    polling.current = true;
    try {
      const chat = await api.listChat(projectId, lastSeq.current);
      if (chat.events.length > 0) {
        lastSeq.current = chat.events[chat.events.length - 1].seq;
        setEvents((previous) => [...previous, ...chat.events]);
      }
      setRun(chat.run);
      setRoadmap(chat.roadmap ?? null);
    } catch (caught) {
      setError(describe(caught, 'Could not load the chat.'));
    } finally {
      polling.current = false;
    }
  }, [api, projectId]);

  const working = isWorking(run);
  const writable = canWrite(run, roadmap);
  // A roadmap that runs changes between its steps too, when no run is working.
  const active = working || roadmap?.status === 'running';
  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), active ? ACTIVE_REFRESH_MS : IDLE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh, active, refreshKey]);

  const items = useMemo(() => timeline(events, log.entries), [events, log.entries]);

  const step = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].type === 'step' && events[i].run_id === run?.run_id) {
        return events[i].text;
      }
    }
    return undefined;
  }, [events, run?.run_id]);
  useEffect(() => {
    onProgress?.({ run, roadmap, step });
  }, [run, roadmap, step, onProgress]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [items.length]);

  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setRun(await api.sendMessage(projectId, { text, ...(deviceId ? { device_id: deviceId } : {}) }));
      if (deviceId) {
        rememberDevice(projectId, deviceId);
      }
      setText('');
      await refresh();
    } catch (caught) {
      setError(describe(caught, 'Could not send the message.'));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (approved: boolean) => {
    if (!run) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      setRun(await api.decideRun(projectId, run.run_id, approved, approved ? designChoiceOf(designChoice) : undefined));
      await refresh();
    } catch (caught) {
      setError(describe(caught, 'Could not send your answer.'));
    } finally {
      setBusy(false);
    }
  };

  const waiting = run?.status === 'awaiting_approval';
  const [designChoice, setDesignChoice] = useState<DesignChoiceState>({ optionId: '', note: '' });
  useEffect(() => setDesignChoice({ optionId: '', note: '' }), [run?.run_id]);
  const hasDesigns = waiting && run.design_options.some((o) => o.state === 'ready');
  const shownError = error ?? log.error;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack
        spacing={1}
        role="log"
        aria-label="Conversation"
        sx={{ flex: 1, minHeight: { xs: 320, md: 0 }, overflowY: 'auto', pr: 1 }}
      >
        {items.length === 0 && (
          <Stack spacing={1.5}>
            <Typography color="text.secondary">
              Say what the app should do. The assistant proposes a roadmap of steps and a design; once you start it,
              each step is built and sent to your device in turn, and you can change the steps by writing here.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try one of these:
            </Typography>
            {SAMPLE_PROMPTS.map((sample) => (
              <Button
                key={sample}
                variant="outlined"
                color="inherit"
                disabled={!writable}
                onClick={() => setText(sample)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderColor: 'divider' }}
              >
                {sample}
              </Button>
            ))}
          </Stack>
        )}
        {items.map((item) =>
          item.kind === 'steps' ? (
            <Steps key={`steps-${item.events[0].seq}`} events={item.events} />
          ) : item.kind === 'log' ? (
            <LogLine
              key={`log-${item.entry.version}-${item.entry.ts}-${item.entry.kind}-${item.entry.message}`}
              entry={item.entry}
            />
          ) : (
            <Event key={`event-${item.event.seq}`} event={item.event} />
          )
        )}
        <Box ref={end} />
      </Stack>
      {working && run && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {workingText(run, roadmap)}
          </Typography>
          <LinearProgress />
        </Box>
      )}
      {shownError && <Alert severity="error">{shownError}</Alert>}
      {hasDesigns && (
        <DesignOptions
          projectId={projectId}
          run={run}
          choice={designChoice}
          onChange={setDesignChoice}
          disabled={busy}
        />
      )}
      {waiting && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" disabled={busy} onClick={() => void decide(false)}>
                Reject
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={busy || !run.device_id || (hasDesigns && designChoice.optionId === '')}
                onClick={() => void decide(true)}
              >
                Start step 1
              </Button>
            </Stack>
          }
        >
          {run.device_id
            ? hasDesigns && designChoice.optionId === ''
              ? 'Choose a design direction above, then start the roadmap, or write what to change in it.'
              : 'Start the roadmap: each step is built and sent to your device in turn. Or write what to change in it.'
            : 'No device was chosen for this request. Choose one and send your request again to build it.'}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'stretch' }}>
        <TextField
          placeholder={
            roadmap && ['running', 'paused', 'awaiting_approval'].includes(roadmap.status)
              ? 'Change the roadmap, or ask'
              : 'Describe your app'
          }
          slotProps={{ htmlInput: { 'aria-label': 'Message' } }}
          multiline
          maxRows={6}
          fullWidth
          size="small"
          sx={{ '& .MuiInputBase-input': { typography: 'body2' } }}
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={!writable}
        />
        <IconButton
          color="primary"
          aria-label="Send"
          disabled={busy || !writable || text.trim() === ''}
          onClick={() => void send()}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, alignSelf: 'stretch', px: 1 }}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
};
